<script setup lang="ts">
import { computed } from 'vue'
import { ElPagination } from 'element-plus'
import { DEFAULT_PAGINATION_LIMIT } from '@/shared/constants'

/**
 * The page-number strip on its own. A list under a page table takes
 * `TablePaginationWrapper` instead, which brings the gap between the two with it.
 *
 * It hides itself when everything fits on one page — a `v-if="total > limit"` at the
 * call site is the same test twice. It also scrolls the window to the top on every page
 * change unless `autoScroll` is off.
 *
 * `update:page` and `pagination` report the same page change; `pagination` is
 * additionally how a `limit` change arrives, which is why `useEntityTable` wires both.
 */
const props = withDefaults(
  defineProps<{
    total: number
    page: number
    limit?: number
    autoScroll?: boolean
    background?: boolean
    /**
     * ElPagination's own layout string. The default is the bare strip — no total, no
     * jumper, no size picker. `page-sizes` is forced empty here, so `update:limit`
     * arrives only from a layout that actually asks for `sizes`.
     */
    layout?: string
  }>(),
  {
    limit: DEFAULT_PAGINATION_LIMIT,
    autoScroll: true,
    background: true,
    layout: 'prev, pager, next'
  }
)

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:limit', value: number): void
  (e: 'pagination', payload: { newPage: number; limit: number }): void
}>()

const pageSize = computed({
  get: () => props.limit ?? DEFAULT_PAGINATION_LIMIT,
  set: (val: number) => emit('update:limit', val)
})

const hidden = computed(() => {
  const perPage = props.limit ?? DEFAULT_PAGINATION_LIMIT
  return props.total <= perPage
})

function handleSizeChange(value: number) {
  emit('pagination', { newPage: props.page, limit: value })
  if (props.autoScroll) window.scrollTo({ top: 0, behavior: 'smooth' })
}

function handleCurrentChange(value: number) {
  emit('update:page', value)
  emit('pagination', { newPage: value, limit: pageSize.value })
  if (props.autoScroll) window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<template>
  <ElPagination
    :key="`p-${props.page}-${total}`"
    v-bind="$attrs"
    v-model:page-size="pageSize"
    :current-page="props.page"
    :class="{ 'pagination-container--hidden': hidden }"
    class="pagination-container"
    :background="background"
    :layout="layout"
    :total="total"
    :page-sizes="[]"
    @size-change="handleSizeChange"
    @current-change="handleCurrentChange"
  />
</template>

<style lang="scss" scoped>
.pagination-container {
  background: none;
}
.pagination-container--hidden {
  display: none;
}
</style>
